/**
 * Offline File Storage
 *
 * Stores files in IndexedDB for offline upload when back online.
 * Works on iOS Safari, Android Chrome, and desktop browsers.
 *
 * Files are stored as ArrayBuffers (more compatible than Blobs on iOS)
 * and reconstructed when needed.
 */

import { v4 as uuidv4 } from 'uuid';
import { createStore, get, set, del, keys, getMany, UseStore } from 'idb-keyval';

const DB_NAME = 'installers-app-files-db';
const STORE_NAME = 'pending-uploads';
const DB_VERSION = 2; // Increment version to trigger upgrade and create the store

/**
 * Custom store for file storage
 * Using a more robust initialization to ensure the object store exists
 */
let fileStore: UseStore;
let dbInitPromise: Promise<void> | null = null;

/**
 * Initialize the IndexedDB database with proper version handling
 * This ensures the object store is created even if the database already exists
 */
async function initializeFileStore(): Promise<void> {
  if (dbInitPromise) {
    return dbInitPromise;
  }

  dbInitPromise = new Promise<void>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(request.error);
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      // Create the object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      db.close(); // Close the connection so idb-keyval can use it
      // Now create the idb-keyval store with the same database/store
      fileStore = createStore(DB_NAME, STORE_NAME);
      resolve();
    };
  });

  return dbInitPromise;
}

/**
 * Get the file store, initializing if needed
 */
async function getFileStore(): Promise<UseStore> {
  if (!fileStore) {
    await initializeFileStore();
  }
  return fileStore;
}

/**
 * Metadata for a pending file upload
 */
export type PendingFileUpload = {
  /** Unique ID for this upload */
  id: string;
  /** Original filename */
  fileName: string;
  /** File MIME type */
  mimeType: string;
  /** File size in bytes */
  size: number;
  /** Entity type this file belongs to (e.g., 'trunk', 'odf', 'outlet') */
  entityType: string;
  /** ID of the entity this file belongs to (can be a temporary client-generated ID) */
  entityId: string;
  /** Property ID for context */
  propertyId: number;
  /** Upload type: 'object' for general objects, 'outlet' for outlets */
  uploadType: 'object' | 'outlet' | 'tpOutlet';
  /** Timestamp when the file was queued */
  createdAt: number;
  /** Status of the upload */
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  /** Error message if failed */
  error?: string;
  /** The final uploaded URL (set after successful upload) */
  uploadedUrl?: string;
  /** Local object URL for immediate display */
  localUrl?: string;
};

/**
 * Storage key prefix for file data
 */
const FILE_DATA_PREFIX = 'file_data_';

/**
 * Storage key prefix for file metadata
 */
const FILE_META_PREFIX = 'file_meta_';

/**
 * Convert a File to ArrayBuffer for storage
 * Using ArrayBuffer instead of Blob for better iOS Safari compatibility
 */
async function fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Convert stored data back to a File
 */
function arrayBufferToFile(buffer: ArrayBuffer, fileName: string, mimeType: string): File {
  const blob = new Blob([buffer], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
}

/**
 * Create a local object URL for immediate display
 * This URL is temporary and will be revoked when the upload completes
 */
function createLocalUrl(buffer: ArrayBuffer, mimeType: string): string {
  const blob = new Blob([buffer], { type: mimeType });
  return URL.createObjectURL(blob);
}

/**
 * Queue a file for offline upload
 *
 * @returns The pending upload metadata including a local URL for immediate display
 */
export async function queueFileUpload(
  file: File,
  entityType: string,
  entityId: string,
  propertyId: number,
  uploadType: 'object' | 'outlet' | 'tpOutlet' = 'object',
): Promise<PendingFileUpload> {
  const store = await getFileStore();
  const id = uuidv4();
  const buffer = await fileToArrayBuffer(file);
  const localUrl = createLocalUrl(buffer, file.type);

  const metadata: PendingFileUpload = {
    id,
    fileName: file.name,
    mimeType: file.type || 'image/jpeg',
    size: file.size,
    entityType,
    entityId,
    propertyId,
    uploadType,
    createdAt: Date.now(),
    status: 'pending',
    localUrl,
  };

  // Store file data and metadata separately
  // This allows us to query metadata without loading large file data
  await set(`${FILE_DATA_PREFIX}${id}`, buffer, store);
  await set(`${FILE_META_PREFIX}${id}`, metadata, store);

  return metadata;
}

/**
 * Get all pending file uploads
 */
export async function getPendingFileUploads(): Promise<PendingFileUpload[]> {
  try {
    const store = await getFileStore();
    const allKeys = await keys(store);
    const metaKeys = allKeys.filter((key) => String(key).startsWith(FILE_META_PREFIX));

    if (metaKeys.length === 0) {
      return [];
    }

    const metadataList = await getMany<PendingFileUpload>(metaKeys, store);
    return metadataList
      .filter((m): m is PendingFileUpload => m != null && m.status === 'pending')
      .sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

/**
 * Get all file uploads (including completed/failed)
 */
export async function getAllFileUploads(): Promise<PendingFileUpload[]> {
  try {
    const store = await getFileStore();
    const allKeys = await keys(store);
    const metaKeys = allKeys.filter((key) => String(key).startsWith(FILE_META_PREFIX));

    if (metaKeys.length === 0) {
      return [];
    }

    const metadataList = await getMany<PendingFileUpload>(metaKeys, store);
    return metadataList.filter((m): m is PendingFileUpload => m != null).sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

/**
 * Get file upload metadata by ID
 */
export async function getFileUpload(id: string): Promise<PendingFileUpload | null> {
  try {
    const store = await getFileStore();
    const metadata = await get<PendingFileUpload>(`${FILE_META_PREFIX}${id}`, store);
    return metadata ?? null;
  } catch {
    return null;
  }
}

/**
 * Get the file data for an upload
 */
export async function getFileData(id: string): Promise<File | null> {
  try {
    const store = await getFileStore();
    const [buffer, metadata] = await Promise.all([
      get<ArrayBuffer>(`${FILE_DATA_PREFIX}${id}`, store),
      get<PendingFileUpload>(`${FILE_META_PREFIX}${id}`, store),
    ]);

    if (!buffer || !metadata) {
      return null;
    }

    return arrayBufferToFile(buffer, metadata.fileName, metadata.mimeType);
  } catch {
    return null;
  }
}

/**
 * Update file upload status
 */
export async function updateFileUploadStatus(
  id: string,
  status: PendingFileUpload['status'],
  uploadedUrl?: string,
  error?: string,
): Promise<void> {
  try {
    const store = await getFileStore();
    const metadata = await get<PendingFileUpload>(`${FILE_META_PREFIX}${id}`, store);
    if (metadata) {
      metadata.status = status;
      if (uploadedUrl) {
        metadata.uploadedUrl = uploadedUrl;
      }
      if (error) {
        metadata.error = error;
      }
      await set(`${FILE_META_PREFIX}${id}`, metadata, store);
    }
  } catch {
    // Ignore errors updating status
  }
}

/**
 * Remove a file upload (both data and metadata)
 */
export async function removeFileUpload(id: string): Promise<void> {
  try {
    const store = await getFileStore();
    const metadata = await get<PendingFileUpload>(`${FILE_META_PREFIX}${id}`, store);

    // Revoke the object URL to free memory
    if (metadata?.localUrl) {
      try {
        URL.revokeObjectURL(metadata.localUrl);
      } catch {
        // Ignore errors revoking URL
      }
    }

    await Promise.all([del(`${FILE_DATA_PREFIX}${id}`, store), del(`${FILE_META_PREFIX}${id}`, store)]);
  } catch {
    // Ignore errors removing upload
  }
}

/**
 * Clean up completed uploads older than the specified age
 */
export async function cleanupCompletedUploads(maxAgeMs: number = 24 * 60 * 60 * 1000): Promise<void> {
  try {
    const uploads = await getAllFileUploads();
    const now = Date.now();

    const toRemove = uploads.filter((upload) => upload.status === 'completed' && now - upload.createdAt > maxAgeMs);

    await Promise.all(toRemove.map((upload) => removeFileUpload(upload.id)));
  } catch {
    // Ignore cleanup errors
  }
}

/**
 * Clean up stale uploads older than the specified age regardless of status
 * This prevents memory leaks from failed or abandoned uploads
 */
export async function cleanupStaleUploads(maxAgeMs: number = 14 * 24 * 60 * 60 * 1000): Promise<void> {
  try {
    const uploads = await getAllFileUploads();
    const now = Date.now();

    const toRemove = uploads.filter((upload) => now - upload.createdAt > maxAgeMs);

    await Promise.all(toRemove.map((upload) => removeFileUpload(upload.id)));
  } catch {
    // Ignore cleanup errors
  }
}

/**
 * Get pending uploads for a specific entity
 */
export async function getPendingUploadsForEntity(entityType: string, entityId: string): Promise<PendingFileUpload[]> {
  const uploads = await getPendingFileUploads();
  return uploads.filter((u) => u.entityType === entityType && u.entityId === entityId);
}

/**
 * Get the display URL for an upload
 * Returns the uploaded URL if available, otherwise the local URL
 */
export function getDisplayUrl(upload: PendingFileUpload): string | undefined {
  return upload.uploadedUrl ?? upload.localUrl;
}

/**
 * Clear all file uploads from storage
 * Used when discarding all local changes
 */
export async function clearAllFileUploads(): Promise<void> {
  try {
    const store = await getFileStore();
    const allKeys = await keys(store);

    // Get all file-related keys (both data and metadata)
    const fileKeys = allKeys.filter(
      (key) => String(key).startsWith(FILE_DATA_PREFIX) || String(key).startsWith(FILE_META_PREFIX),
    );

    // First, revoke any blob URLs to free memory
    const metaKeys = fileKeys.filter((key) => String(key).startsWith(FILE_META_PREFIX));
    if (metaKeys.length > 0) {
      const metadataList = await getMany<PendingFileUpload>(metaKeys, store);
      metadataList.forEach((metadata) => {
        if (metadata?.localUrl) {
          try {
            URL.revokeObjectURL(metadata.localUrl);
          } catch {
            // Ignore errors revoking URL
          }
        }
      });
    }

    // Delete all file-related keys
    await Promise.all(fileKeys.map((key) => del(key, store)));
  } catch {
    // Ignore errors clearing uploads
  }
}

/**
 * Recreate a blob URL from stored file data
 * This is needed after navigation/reload since blob URLs don't persist
 */
export async function recreateBlobUrl(uploadId: string): Promise<string | null> {
  try {
    const store = await getFileStore();
    const [buffer, metadata] = await Promise.all([
      get<ArrayBuffer>(`${FILE_DATA_PREFIX}${uploadId}`, store),
      get<PendingFileUpload>(`${FILE_META_PREFIX}${uploadId}`, store),
    ]);

    if (!buffer || !metadata) {
      return null;
    }

    // Revoke old URL before creating new one to prevent memory leak
    if (metadata.localUrl) {
      try {
        URL.revokeObjectURL(metadata.localUrl);
      } catch {
        // Ignore errors revoking URL
      }
    }

    // Create a new blob URL from the stored data
    const blob = new Blob([buffer], { type: metadata.mimeType });
    const newBlobUrl = URL.createObjectURL(blob);

    // Update the metadata with the new blob URL
    metadata.localUrl = newBlobUrl;
    await set(`${FILE_META_PREFIX}${uploadId}`, metadata, store);

    return newBlobUrl;
  } catch {
    return null;
  }
}
